import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { useRef, useMemo } from "react";
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "./config";
import Color from "color";
import { defaultDarkPreset } from "./constants";
import { CommunicationStore, createCommunicationStore, StorageArea, StorageNamespace } from "./communication";
import { preloadedSettings } from "./preloadedSettings";


type CommunicationRequest = {
    type: string;
    payload: any;
    id?: string;
}

const CommunicationContext = createContext<any>(null);

type CommunicationContextType = {
    settings: any;
    trigger: number;
    authenticatedClient: () => Promise<any>;
    authenticatedFetch: (url: string, options: RequestInit) => Promise<Response>;
    sendMessage: (message: any) => void;
    updateOptions: (updates: any, namespace?: StorageNamespace) => void;
    showError: (message: string, type: string) => void;
    useRerenderListener: (pattern: string[], namespace?: StorageNamespace) => { "options": StorageArea, "trigger": number };
    loadAuthModal: (type: string, options?: any) => void;
    openRestrictedDialog: (type: string, options?: any) => void;
    getOptions: (namespace?: StorageNamespace) => StorageArea;
    restrictedActionCheck: (restrictedTypes: PlanTypes[], onRestricted?: (() => void) | null) => boolean;
    trackEvent: (message: string) => void;
    withEventCooldown: (event: string, callback: () => void, cooldown: number) => void;
    openAssignmentCreator: (assignment: any, date?: Date | null) => void;
    openScheduleEditor: () => void;
    sendMessageAndListen: (message: any) => Promise<any>;
    googleCalendar: any;
    microsoftCalendar: any;
    openCalendarSync: () => void;
    calendarSync: any;
    closeModal: () => void;
    checkGate: (highlight: string) => Promise<{ restricted: boolean }>;
    checkGateBoolean: (highlight: string) => Promise<{ restricted: boolean }>;
    openGate: (highlight: string) => void;
    callExtensionFunc: (func: string, args: any) => void;
    loadSharedComponent: (component: string, props?: any) => void;
    getValidCalendarToken: () => Promise<string>;
    origin: string;
    sendToBackgroundScript: (payload: any) => Promise<void>;
    forceRefreshToken: () => void;
    requestValidToken: () => Promise<string>
    navigatePage: (page: string, urlParams: Record<string, string>) => void;
}

export const useCommunication = (): CommunicationContextType => {
    const context = useContext(CommunicationContext);
    if (!context) {
        throw new Error("useCommunication must be used within a CommunicationProvider");
    }
    return context;
}

export const CommunicationProvider = ({ children, requiresLocalStorage = false }: { children: React.ReactNode, requiresLocalStorage?: boolean }) => {

    //const [settings, setSettings] = useState<any>(null);
    const [settingsChanges, setSettingsChanges] = useState<Record<string, number>>({});
    const [ready, setReady] = useState<boolean>(false);
    const previousSettings = useRef<any>(null);

    const origin = new URLSearchParams(window.location.search).get("origin");

    const settings = useRef<CommunicationStore>(createCommunicationStore());
    const messageListeners = useRef<Record<string, (token: string) => void>>({});
    

    const usePreloadedSettings = () => {
        settings.current.setSettings(preloadedSettings);
        setReady(true);
    }

    function requestValidToken() {
        const correlationId = Math.random().toString(36).substring(2, 9); // Simple unique ID
        const request = {
            type: 'REQUEST_VALID_TOKEN',
            id: correlationId,
        };

        return new Promise((resolve, reject) => {
            // Save the resolver for this specific request ID
            messageListeners.current[correlationId] = resolve;

            // Send the message to the parent
            window.parent.postMessage(request, '*');

            // Optional: Add a timeout to reject the promise if no response is received
            // setTimeout(() => { delete messageListeners[correlationId]; reject(new Error('Timeout')); }, 10000);
        });
    }

    const authenticatedClient = async (): Promise<SupabaseClient> => {
        const validToken = await requestValidToken();

        const client = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
            global: {
                headers: {
                    Authorization: `Bearer ${validToken}`
                }
            }
        });

        return client;
    }

    const authenticatedFetch = async (url: string, options: RequestInit = {}) => {

        const validToken = await requestValidToken();

        // Check if body is FormData - if so, don't set Content-Type (browser will set it automatically)
        const isFormData = options.body instanceof FormData;

        const headers: Record<string, string> = {
            Authorization: `Bearer ${validToken}`,
            ...options.headers,
        };

        // Only set Content-Type for JSON requests, not FormData
        if (!isFormData) {
            headers["Content-Type"] = "application/json";
        }

        return await fetch(url, {
            method: options.method || "GET",
            headers,
            body: isFormData ? options.body : (options.body ? JSON.stringify(options.body) : undefined),
        });
    }


    // sends a message to the extension without expecting a response
    const sendMessage = useCallback((message: CommunicationRequest) => {
        console.log("[commms] sendMessage to extension", message);
        window.parent.postMessage(message, "*");
    }, []);

    // sends a message and listens for a response
    const sendMessageAndListen = useCallback((message: CommunicationRequest) => {
        return new Promise((resolve, reject) => {
            const correlationId = Math.random().toString(36).substring(2, 9);
            sendMessage({ ...message, id: correlationId });
            messageListeners.current[correlationId] = resolve;
        });
    }, [sendMessage]);

    // sends a message directly to the background script via chrome.runtime.sendMessage
    const sendToBackgroundScript = useCallback(async (payload: any) => {
        sendMessage({
            type: "SEND_TO_BACKGROUND",
            payload: payload,
        })
    }, [sendMessage]);

    useEffect(() => {
        const messageListener = (event: any) => {

            console.log("[commms] message received from extension", event);

            const type = event?.data?.type || "";
            const data = event?.data || {};
            const payload = data?.payload || {};

            console.log("type", type, type === "CANVAS_DATA_RESPONSE");

            // Check if the message is a response to an active request
            if (type === "CALL_EXTENSION_FUNC_RESPONSE") {
                console.log("[commms] CALL_EXTENSION_FUNC_RESPONSE received", messageListeners.current, data.id);
                const resolve = messageListeners.current[data.id];
                if (!resolve) return;
                delete messageListeners.current[data.id]; // Clean up the listener
                resolve(data.payload);
            } else if (type === 'REQUEST_VALID_TOKEN_RESPONSE' && data.id && messageListeners.current[data.id]) {
                const resolve = messageListeners.current[data.id];
                delete messageListeners.current[data.id]; // Clean up the listener
                // Resolve the promise with the new token
                resolve(data.payload);
            } else if (type === "REQUEST_SETTINGS_RESPONSE") {
                console.log("[commms] SETTINGS_UPDATE RECEIVED SUCCESSFULLY! UPDATING EXTENSION !!!!", payload);
                settings.current.setSettings(payload);
                sendMessage({
                    type: "CONFIRM_SETTINGS_UPDATE",
                    payload: {}
                });
                settings.current.initListeners();
                setReady(true);
            } else if (type === "SETTINGS_UPDATE_LOCAL") {
                settings.current.updateSettings("local", payload);
            } else if (type === "SETTINGS_UPDATE") {
                settings.current.updateSettings("sync", payload);
            } else if (type === "CANVAS_DATA_RESPONSE") {
                const id = data.id;
                const response = data.payload;
                const resolve = messageListeners.current[id];
                console.log("[message response]", id, response, resolve);
                if (!resolve) return;
                delete messageListeners.current[id]; // Clean up the listener
                resolve(response);
            } else if (type === "COMMUNICATION_LAYER_MESSAGE_FORWARD") {
                sendMessage(data.payload);
            }
        }

        window.addEventListener('message', messageListener);

        sendMessage({
            type: "REQUEST_SETTINGS",
            payload: {
                "local": requiresLocalStorage,
            },
        });

        return () => {
            window.removeEventListener('message', messageListener);
        }
    }, []);
    

    const {
        updateOptions,
        showError,
        trackEvent,
        checkGate,
        checkGateBoolean,
        openGate,
        openAssignmentCreator,
        loadAuthModal,
        openRestrictedDialog,
        loadSharedComponent,
        removeSharedComponent,
        callExtensionFunc,
        getValidCalendarToken,
        forceRefreshToken,
        navigatePage
    } = useMemo(() => {

        const callExtensionFunc = (func: string, args: any) => {
            sendMessage({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: func,
                    args: args
                }
            });
        }

        return {
            callExtensionFunc: callExtensionFunc,
            updateOptions: (updates: any, namespace: StorageNamespace = "sync") => {
                callExtensionFunc("updateOptions", { namespace: namespace, updates: updates });
            },
            showError: (message: string, type: string) => {
                callExtensionFunc("showError", { message: message, type: type });
            },
            trackEvent: (message: string) => {
                callExtensionFunc("trackEvent", { message: message });
            },
            checkGate: async (highlight: string) => {
                return await sendMessageAndListen({
                    type: "CALL_EXTENSION_FUNC",
                    payload: {
                        func: "checkGate",
                        args: { highlight: highlight }
                    }
                });
            },
            checkGateBoolean: async (highlight: string) => {
                return await sendMessageAndListen({
                    type: "CALL_EXTENSION_FUNC",
                    payload: {
                        func: "checkGateBoolean",
                        args: { highlight: highlight }
                    }
                });
            },
            openGate: (highlight: string) => {
                callExtensionFunc("openGate", { highlight: highlight });
            },
            openAssignmentCreator: (assignment: any, date?: Date | null) => {
                callExtensionFunc("loadSharedComponent", { component: "assignmentCreator", props: { assignment, initialDate: date } });
            },
            loadAuthModal: (type: string, options?: any) => {
                callExtensionFunc("loadAuthModal", { type: type, options: options });
            },
            openRestrictedDialog: (type: string, options?: any) => {
                callExtensionFunc("openRestrictedDialog", { type: type, options: options });
            },
            loadSharedComponent: (component: string, props?: any) => {
                callExtensionFunc("loadSharedComponent", { component: component, props: props || {}});
            },
            removeSharedComponent: (component: string) => {
                callExtensionFunc("removeSharedComponent", { component: component });
            },
            forceRefreshToken: () => {
                callExtensionFunc("refreshAccessToken", { });
            },
            getValidCalendarToken: async () => {
                const response: any = await sendMessageAndListen({
                    type: "CALL_EXTENSION_FUNC",
                    payload: {
                        func: "getValidCalendarToken",
                        args: {}
                    }
                });
                if (response.success) return response.token;
            },
            navigatePage: (page: "home" | "assignments" | "notes" | "grades" | "study", urlParams: Record<string, string>) => {
                sendMessage({
                    type: "CALL_EXTENSION_FUNC",
                    payload: {
                    func: "navigatePage",
                    args: {
                        page: page,
                        urlParams: urlParams,
                    },
                    },
                });
            }
        }
    }, [sendMessage, sendMessageAndListen]);

    const openScheduleEditor = () => {
        sendMessage({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                "func": "openScheduleEditor",
                args: {}
            }
        })
    }

    // Google Calendar Sync Functions
    const googleCalendar = useMemo(() => ({
        checkAndRefreshToken: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "checkAndRefreshGoogleToken",
                    args: {}
                }
            });
        },
        createBetterCampusCalendar: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "createBetterCampusCalendar",
                    args: {}
                }
            });
        },
        ensureBetterCampusCalendar: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "ensureBetterCampusCalendar",
                    args: {}
                }
            });
        },
        fetchAndStoreCalendars: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "fetchAndStoreCalendars",
                    args: {}
                }
            });
        },
        listCalendars: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "listCalendars",
                    args: {}
                }
            });
        },
        createEvent: async (calendarId: string, event: any) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "createGoogleEvent",
                    args: { calendarId, event }
                }
            });
        },
        updateEvent: async (calendarId: string, eventId: string, event: any) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "updateGoogleEvent",
                    args: { calendarId, eventId, event }
                }
            });
        },
        deleteEvent: async (calendarId: string, eventId: string) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "deleteGoogleEvent",
                    args: { calendarId, eventId }
                }
            });
        },
        deleteAllPushedEvents: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "deleteAllPushedEvents",
                    args: {}
                }
            });
        },
        isSyncInProgress: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "isSyncInProgress",
                    args: {}
                }
            });
        },
        cancelCurrentSync: () => {
            sendMessage({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "cancelCurrentSync",
                    args: {}
                }
            });
        },
        disconnectGoogleCalendar: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "disconnectGoogleCalendar",
                    args: {}
                }
            });
        },
        pushAssignmentDueDates: async (assignments: any[]) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "pushAssignmentDueDates",
                    args: { assignments }
                }
            });
        },
        handleInitialSync: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialSync",
                    args: {}
                }
            });
        },
        handleInitialDueDates: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialDueDates",
                    args: {}
                }
            });
        },
        handleInitialPlanned: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialPlanned",
                    args: {}
                }
            });
        },
        handleInitialClassTimes: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialClassTimes",
                    args: {}
                }
            });
        },
        pushAssignmentPlannedDates: async (assignments: any[]) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "pushAssignmentPlannedDates",
                    args: { assignments }
                }
            });
        },
        refreshClassTimes: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "refreshClassTimes",
                    args: {}
                }
            });
        },
        triggerAutoSyncWithCooldown: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "triggerAutoSyncWithCooldown",
                    args: {}
                }
            });
        }
    }), [sendMessageAndListen]);

    // Microsoft Calendar Sync Functions
    const microsoftCalendar = useMemo(() => ({
        checkAndRefreshToken: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "checkAndRefreshMicrosoftToken",
                    args: {}
                }
            });
        },
        createBetterCampusCalendar: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "createBetterCampusMicrosoftCalendar",
                    args: {}
                }
            });
        },
        ensureBetterCampusCalendar: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "ensureBetterCampusMicrosoftCalendar",
                    args: {}
                }
            });
        },
        fetchAndStoreCalendars: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "fetchAndStoreMicrosoftCalendars",
                    args: {}
                }
            });
        },
        listCalendars: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "listMicrosoftCalendars",
                    args: {}
                }
            });
        },
        createEvent: async (calendarId: string, event: any) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "createMicrosoftEvent",
                    args: { calendarId, event }
                }
            });
        },
        updateEvent: async (calendarId: string, eventId: string, event: any) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "updateMicrosoftEvent",
                    args: { calendarId, eventId, event }
                }
            });
        },
        deleteEvent: async (calendarId: string, eventId: string) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "deleteMicrosoftEvent",
                    args: { calendarId, eventId }
                }
            });
        },
        deleteAllPushedEvents: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "deleteAllPushedMicrosoftEvents",
                    args: {}
                }
            });
        },
        isSyncInProgress: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "isMicrosoftSyncInProgress",
                    args: {}
                }
            });
        },
        cancelCurrentSync: () => {
            sendMessage({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "cancelCurrentMicrosoftSync",
                    args: {}
                }
            });
        },
        disconnectMicrosoftCalendar: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "disconnectMicrosoftCalendar",
                    args: {}
                }
            });
        },
        pushAssignmentDueDates: async (assignments: any[]) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "pushAssignmentDueDatesToMicrosoft",
                    args: { assignments }
                }
            });
        },
        handleInitialSync: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialMicrosoftSync",
                    args: {}
                }
            });
        },
        handleInitialDueDates: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialMicrosoftDueDates",
                    args: {}
                }
            });
        },
        handleInitialPlanned: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialMicrosoftPlanned",
                    args: {}
                }
            });
        },
        handleInitialClassTimes: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "handleInitialMicrosoftClassTimes",
                    args: {}
                }
            });
        },
        pushAssignmentPlannedDates: async (assignments: any[]) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "pushAssignmentPlannedDatesToMicrosoft",
                    args: { assignments }
                }
            });
        },
        refreshClassTimes: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "refreshMicrosoftClassTimes",
                    args: {}
                }
            });
        },
        triggerAutoSyncWithCooldown: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "triggerMicrosoftAutoSyncWithCooldown",
                    args: {}
                }
            });
        }
    }), [sendMessageAndListen]);

    const openCalendarSync = () => {
        sendMessage({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                "func": "openCalendarSync",
                args: {}
            }
        })
    }

    // Calendar Sync Functions (Microsoft + Google)
    const calendarSync = useMemo(() => ({
        syncAssignment: async (assignmentId: string, assignmentData: any, syncType: 'planned' | 'dueDate' | 'both' = 'both') => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "syncAssignmentToCalendars",
                    args: { assignmentId, assignmentData, syncType }
                }
            });
        },
        triggerPullSync: async () => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "triggerCalendarPullSync",
                    args: {}
                }
            });
        },
        deleteAssignment: async (assignmentId: string) => {
            return sendMessageAndListen({
                type: "CALL_EXTENSION_FUNC",
                payload: {
                    func: "deleteAssignmentFromCalendars",
                    args: { assignmentId }
                }
            });
        }
    }), [sendMessageAndListen]);


    const withEventCooldown = useCallback((message: string, callback: () => void, cooldown: number) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                callback();
                resolve(true);
            }, cooldown);
        });
    }, []);

    const getOptions = useCallback((namespace: StorageNamespace = "sync") => {
        return settings.current.getSnapshot()[namespace] || {};
    }, [settings]);

    const useRerenderListener = useCallback((patterns: string[], namespace: StorageNamespace = "sync") => {
        const [localTrigger, setLocalTrigger] = useState<number>(0);
        //const options = useRef<StorageArea>(getOptions(namespace));

        //console.log("[commms] useRerenderListener called", getOptions(namespace), options.current,patterns, namespace);

        useEffect(() => {

            const subscribe = () => {
                console.log("[commms] subscribe called", patterns);
                //options.current = getOptions(namespace);
                setLocalTrigger(prev => prev + 1);
            }

            // Subscribe to the store with the pattern
            patterns.forEach(p =>
                settings.current.subscribe(subscribe, p)
            );

            // Cleanup subscriptions
            return () => {
                patterns.forEach(p =>
                    settings.current.unsubscribe(subscribe, p)
                );
            };
        }, [patterns.join(','), namespace]);

        const options = useMemo(() => getOptions(namespace), [namespace, localTrigger]);
        console.log("[commms] useRerenderListener options", options);

        return { "options": options, "trigger": localTrigger };
    }, [getOptions]);

    // Use a stable empty object to avoid breaking memoization

    const closeModal = useCallback(() => {
        sendMessage({
            type: "EXIT",
            payload: {}
        })
    }, [sendMessage]);

    const value = useMemo(() => ({
        ready,
        settings: settings.current.getSnapshot(),
        settingsChanges,
        authenticatedClient,
        authenticatedFetch,
        sendMessage,
        sendMessageAndListen,
        sendToBackgroundScript,
        updateOptions,
        useRerenderListener,
        loadAuthModal,
        openRestrictedDialog,
        showError,
        getOptions,
        trackEvent,
        withEventCooldown,
        openAssignmentCreator,
        openScheduleEditor,
        googleCalendar,
        microsoftCalendar,
        openCalendarSync,
        calendarSync,
        closeModal,
        checkGate,
        checkGateBoolean,
        openGate,
        loadSharedComponent,
        removeSharedComponent,
        callExtensionFunc,
        origin,
        getValidCalendarToken,
        forceRefreshToken,
        requestValidToken,
        navigatePage
    }), [settingsChanges, authenticatedClient, authenticatedFetch, sendMessage, sendMessageAndListen, sendToBackgroundScript, updateOptions, useRerenderListener, loadAuthModal, openRestrictedDialog, showError, getOptions, trackEvent, withEventCooldown, openAssignmentCreator, googleCalendar, microsoftCalendar, calendarSync, closeModal, checkGate, loadSharedComponent])


    if (!ready) {
        if (import.meta.env.VITE_ENV === "dev") {
            return <UsePreloadedSettings usePreloadedSettings={usePreloadedSettings} />;
        }
        return null;
    }

    return (
        <CommunicationContext.Provider value={value}>
            <div className="h-screen w-full">
                {children}
            </div>
        </CommunicationContext.Provider>
    )
}


const UsePreloadedSettings = ({ usePreloadedSettings }: { usePreloadedSettings: () => void }) => {

    return (
        <div className="h-full w-full flex items-center justify-center">
            <div className="flex flex-col items-center justify-center">
                <p className="text-2xl font-bold">Use preloaded settings?</p>
                <button className="bg-blue-500 text-white px-4 py-2 rounded-md" onClick={usePreloadedSettings}>Go</button>
            </div>
        </div>
    )
}